import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { fmtDateTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { canUploadWO } from '@/lib/roles';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { useBulkSelection } from '@/lib/useBulkSelection';
import { Plus, PauseCircle, Pencil } from 'lucide-react';
import { WOEditDialog } from '@/components/wo/WOEditDialog';

const BULK_ROLES = ['owner', 'operations', 'accounts'];
const STATUS_CHIP = {
  uploaded: { cls: '', label: 'Uploaded' },
  parsed: { cls: 'chip-info', label: 'Parsed — awaiting verify' },
  verified: { cls: 'chip-warning', label: 'Verified — awaiting activation' },
  activated: { cls: 'chip-success', label: 'Activated' },
};

export default function WorkOrdersPage() {
  const { user } = useAuth();
  const [wos, setWos] = useState([]);
  const [editing, setEditing] = useState(null);   // iter41 — WO id being edited
  const sel = useBulkSelection(wos);
  const canBulk = BULK_ROLES.includes(user?.role);
  const canEdit = ['owner', 'operations', 'accounts'].includes(user?.role);
  // Accounts can view + enter money + approve, but uploading a raw WO PDF
  // stays with Owner + Ops. Hide the two "add WO" CTAs for anyone who can't.
  const canUpload = canUploadWO(user?.role);

  const load = () => api.get('/work_orders').then(r => setWos(r.data || [])).catch(e => toast.error(parseErr(e)));
  useEffect(() => { load(); }, []);
  useAutoRefetch(load, { collections: ['work_orders', 'master_projects'] });

  return (
    <AppShell breadcrumbs={[{ label: 'Work Orders' }]}>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="work-orders-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Work Orders</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">Upload, AI-parse, verify, and activate.</p>
          </div>
          <div className="flex items-center gap-2">
            {canUpload && (
              <>
                <Button asChild variant="outline" data-testid="wo-manual-cta">
                  <Link to="/work-orders/manual"><Plus size={14} className="mr-1" />Add manually</Link>
                </Button>
                <Button asChild data-testid="wo-upload-cta">
                  <Link to="/work-orders/upload"><Plus size={14} className="mr-1" />Upload & AI parse</Link>
                </Button>
              </>
            )}
          </div>
        </div>

        {canBulk && (
          <BulkActionBar
            module="work_orders" moduleLabel="Work Orders"
            selectedIds={sel.selectedIds} onClear={sel.clear} onApplied={load}
            needsApproval={user?.role !== 'owner'}
            editableFields={['status', 'values_status', 'record_state']}
          />
        )}

        <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto" style={{ borderColor: 'hsl(var(--border))' }}>
          <table className="tbl">
            <thead>
              <tr>
                {canBulk && (
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={sel.allChecked}
                      ref={el => el && (el.indeterminate = sel.indeterminate)}
                      onChange={sel.toggleAll}
                      aria-label="Select all"
                      data-testid="bulk-select-all-work_orders"
                    />
                  </th>
                )}
                <th>File</th><th>Client</th><th>WO #</th><th>Status</th><th>Updated</th><th></th>
              </tr>
            </thead>
            <tbody>
              {wos.length === 0 && <tr><td colSpan={canBulk ? 7 : 6} className="text-center py-6 text-[hsl(var(--foreground-muted))]">No work orders yet.</td></tr>}
              {wos.map(w => {
                const s = STATUS_CHIP[w.status] || {};
                return (
                  <tr key={w.id} data-testid={`wo-row-${w.id}`}>
                    {canBulk && (
                      <td>
                        <input
                          type="checkbox"
                          checked={sel.isSelected(w.id)}
                          onChange={() => sel.toggle(w.id)}
                          aria-label={`Select ${w.original_filename || w.id}`}
                          data-testid={`wo-select-${w.id}`}
                        />
                      </td>
                    )}
                    <td>{w.original_filename || '—'}</td>
                    <td>{w.client_name || w.client_hint || '—'}</td>
                    <td className="font-mono text-[12px]">{w.wo_number || '—'}</td>
                    <td>
                      <span className={`chip ${s.cls}`}>{s.label || w.status}</span>
                      {w.record_state && w.record_state !== 'active' && (
                        <span className="chip ml-1" style={{ fontSize: '10px', background: 'hsl(var(--warning)/.14)', color: 'hsl(var(--warning))' }}>
                          <PauseCircle size={9} className="inline mr-0.5" />{w.record_state === 'on_hold' ? 'On hold' : w.record_state}
                        </span>
                      )}
                    </td>
                    <td className="text-[12px] text-[hsl(var(--foreground-muted))]">{fmtDateTime(w.updated_at)}</td>
                    <td className="text-right">
                      {canEdit && (
                        <Button size="sm" variant="ghost"
                                onClick={() => setEditing(w.id)}
                                data-testid={`wo-edit-btn-${w.id}`}
                                className="mr-1">
                          <Pencil size={12} className="mr-1" />Edit
                        </Button>
                      )}
                      {w.storage_path && (
                        <Link to={`/work-orders/${w.id}/verify`} className="text-[13px] underline">Open</Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {editing && (
        <WOEditDialog woId={editing}
                      open={true}
                      onClose={() => setEditing(null)}
                      onSaved={() => { setEditing(null); load(); }} />
      )}
    </AppShell>
  );
}
